import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './styles.module.scss';
import { selectItem } from '../../store/slices/timelineSlice';
import { seek } from '../../store/slices/playbackSlice';
import { setTrimModalData, setFocusedSectionTitleId, setHoveredSectionTitleId } from '../../store/slices/editorUISlice';
import { selectUploadState, cancelUpload, retryUpload } from '../../store/slices/uploadProgressSlice';
import { updateAsset } from '../../store/slices/assetsSlice';
import DeleteSectionIcon from '../../../../components/CreateMeetingFooter/DeleteSectionIcon';
import Tooltip from '../../../../components/Tooltip';
import SectionService from '../../../../services/SectionService';
import getUserPhotoUrl from '../../../../helpers/getUserPhotoUrl';
import usericon from '../../../../assets/images/usericon.webp';
import playIcon from '../../../../assets/images/section-menu/play.svg';
import editIcon from '../../../../assets/images/section-menu/edit.svg';
import saveIcon from '../../../../assets/images/section-menu/star.svg';
import notesIcon from '../../../../assets/images/section-menu/notes.svg';
import questionSectionImg from '../../../../assets/images/section-menu/question-section.png';
import assignIcon from '../../../../assets/images/section-menu/section.png';
import checkmarkIcon from '../../../../assets/images/view-meeting/checkmark.svg';
import { getDocumentIcon } from '../../lib/getDocumentIcon';
import CircularProgress from '../CircularProgress';
import TemplatesIcon from '../../../../components/TemplatesIcon';
import PlaceholderPatternOverlay from '../PlaceholderPatternOverlay';

const SECTION_WIDTH = 235;
const GAP = 50;

export default function TimelineSectionCard({
  item,
  itemIndex,
  asset,
  currentItem,
  isHovered,
  isVideo,
  isDragged,
  isDragOverBefore,
  isDragOverAfter,
  isAllowedToDrag,
  isPreviewMode,
  isEditMode,
  pendingSaveItemIds,
  savedPopup,
  focusedSectionTitleId,
  hoveredSectionTitleId,
  handleSectionTitleChange,
  handleOpenEditQuestion,
  handleOpenEditText,
  handleOpenEditPlaceholder,
  handleDragStart,
  handleDragEnd,
  setHoveredItemId,
  setPendingSaveItemIds,
  setSavedPopup,
  removeTimelineItem,
  user,
  fetchedSections,
  isCreator = true, // Default to true for backward compatibility
  isCompleted = false,
  isTrainingView = false,
  isTemplateMode = false,
  onPlaceholderAddContent,
}) {
  const dispatch = useDispatch();
  const uploadState = useSelector(selectUploadState(asset.id));
  const [isUserIconHovered, setIsUserIconHovered] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Check if upload is in progress
  const isUploading = uploadState && (uploadState.status === 'uploading' || uploadState.status === 'queued');
  // Check if upload is completed (to keep checkmark centered)
  const isUploadCompleted = uploadState && uploadState.status === 'completed';

  // Check if this section was created by the current user (for contributors)
  const isSectionCreatedByUser = () => {
    if (isCreator) return true; // Creators can interact with all sections
    if (!user) return false;
    
    const sectionId = item.metadata?.sectionId;
    
    // If no sectionId, this is a newly added section (not yet saved to backend)
    // For contributors, newly added sections (source_type: 'upload') are assumed to be created by them
    if (!sectionId) {
      return asset?.source_type === 'upload' && asset?.type !== 'assigned-section';
    }
    
    // If sectionId exists, check fetchedSections or asset metadata
    if (fetchedSections) {
      const originalSection = fetchedSections.find((s) => s.id === sectionId);
      if (originalSection) {
        return originalSection.userId === user.id;
      }
    }
    
    // Fallback: check asset metadata for userId
    if (asset?.metadata?.userId) {
      return asset.metadata.userId === user.id;
    }
    
    return false;
  };

  // In training view, disable all interactions (similar to non-interactive sections for contributors)
  const canInteractWithSection = isTrainingView ? false : isSectionCreatedByUser();
  // In training view, completed sections should have full opacity to show checkmarks clearly
  // During upload, set opacity to 0.5
  let sectionOpacity = 0.5;
  if (isTrainingView && isCompleted) {
    sectionOpacity = 1;
  } else if ((isCreator || canInteractWithSection) && !isTrainingView && !isUploading) {
    sectionOpacity = 1;
  } else if (isUploading) {
    sectionOpacity = 0.5;
  }

  // Determine if user icon should be shown and which user data to use
  const getUserIconData = () => {
    // Show badges in both edit mode and viewer mode when we have fetchedSections and user
    if (!fetchedSections || !user) return null;

    const sectionId = item.metadata?.sectionId;
    if (!sectionId) return null;

    const originalSection = fetchedSections.find((s) => s.id === sectionId);
    if (!originalSection) return null;

    // For assigned-section: always show assigned user
    if (asset.type === 'assigned-section') {
      // Try asset metadata first, then fall back to section data
      const photoPath = asset.metadata?.assignedPhotoPath || originalSection.assigned?.photoPath;
      const name = asset.metadata?.assignedName || asset.metadata?.assignedEmail || originalSection.assigned?.name || originalSection.assignedEmail || 'User';
      if (photoPath || name) {
        return { photoPath, name };
      }
      return null;
    }

    // For contributed sections: show if section was created by someone else
    if (originalSection.userId && originalSection.userId !== user.id) {
        // Section was contributed by someone else
      // Try asset metadata first, then fall back to section data
        const photoPath = asset.metadata?.creatorPhotoPath || originalSection.creator?.photoPath;
        const name = asset.metadata?.creatorName || originalSection.creator?.name;
        if (photoPath || name) {
          return { photoPath, name };
      }
    }

    return null;
  };

  const userIconData = getUserIconData();
  const isLockedPlaceholderTitle = asset.type === 'placeholder' && !isTemplateMode;
  const titleColor = isLockedPlaceholderTitle ? '#6B7280' : '#000';

  const handleCardClick = () => {
    const assetForItem = asset;
    
    // In training view, only allow seeking (no editing)
    if (isTrainingView) {
      dispatch(selectItem(item.id));
      dispatch(seek(item.start_time));
      return;
    }
    
    // For non-playable types (question, text), open modal AND seek to section start
    if (assetForItem?.type === 'question') {
      dispatch(selectItem(item.id));
      dispatch(seek(item.start_time));
      handleOpenEditQuestion(assetForItem, item);
    } else if (assetForItem?.type === 'text') {
      dispatch(selectItem(item.id));
      dispatch(seek(item.start_time));
      handleOpenEditText(assetForItem, item);
    } else if (assetForItem?.type === 'assigned-section') {
      dispatch(selectItem(item.id));
      dispatch(seek(item.start_time));
      // If user is assigned to this section, allow them to add content
      // This will be handled by the parent component via onAddContent callback
      // For now, just select and seek (the renderer will show the Add Content button)
    } else {
      dispatch(selectItem(item.id));
      dispatch(seek(item.start_time));
    }
  };

  return (
    <>
      <li 
        data-item-id={item.id}
        data-item-index={itemIndex}
        style={{ 
          display: 'inline-block', 
          marginRight: 0,
          position: 'relative',
        }}
        onMouseEnter={() => {
          // Only allow hover interactions if contributor can interact with this section
          if (isCreator || canInteractWithSection) {
            setHoveredItemId(item.id);
          }
        }}
        onMouseLeave={() => setHoveredItemId(null)}
      >
        {/* Draggable wrapper - wraps entire section for drag and drop */}
        <div
          draggable={isAllowedToDrag}
          onDragStart={(e) => {
            e.stopPropagation();
            handleDragStart(e, itemIndex);
          }}
          onDragEnd={handleDragEnd}
          style={{
            position: 'relative',
            width: SECTION_WIDTH,
            height: 120, // Match card height for vertical centering
            opacity: isDragged ? 0.5 : sectionOpacity, // Use sectionOpacity for non-interactive sections
            cursor: isAllowedToDrag ? 'grab' : 'default',
            transition: isDragged ? 'none' : 'opacity 0.2s',
          }}
        >
          {/* Drop indicator line - show before this section (gap before itemIndex) */}
          {isDragOverBefore && !isDragged && (
            <div style={{
              position: 'absolute',
              left: -(GAP / 2) - 2,
              top: 0,
              bottom: 0,
              width: 4,
              background: '#F8585C',
              zIndex: 20,
              pointerEvents: 'none',
              borderRadius: 2,
            }} />
          )}
          {/* Drop indicator line - show after this section (gap after itemIndex) */}
          {isDragOverAfter && !isDragged && (
            <div style={{
              position: 'absolute',
              left: SECTION_WIDTH + (GAP / 2) - 2,
              top: 0,
              bottom: 0,
              width: 4,
              background: '#F8585C',
              zIndex: 20,
              pointerEvents: 'none',
              borderRadius: 2,
            }} />
          )}
          {/* Delete disabled in preview mode. Contributors can delete sections they created */}
          {!isPreviewMode && (isCreator || canInteractWithSection) && (
            <DeleteSectionIcon
              itemId={item.id}
              onClick={(e) => {
                e.stopPropagation();

                // Check if there's an upload in progress for this asset
                if (uploadState && (uploadState.status === 'uploading' || uploadState.status === 'queued')) {
                  // Cancel the upload
                  dispatch(cancelUpload({ assetId: asset.id }));
                }

                removeTimelineItem(item.id);
              }}
            />
          )}
          {/* Play icon - for video items */}
          {isVideo && !isPreviewMode && (
            <img 
              src={playIcon} 
              alt="Play" 
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 55,
                height: 55,
                zIndex: 5,
                pointerEvents: 'none',
              }}
            />
          )}
          <button type="button" style={{
            width: SECTION_WIDTH,
            height: 120,
            borderRadius: 16,
            border: 'none',
            background: currentItem?.id === item.id ? 'transparent' : '#ffffff',
            overflow: 'hidden',
            cursor: (isTrainingView && !isCompleted) ? 'default' : 'pointer',
            padding: 0,
            position: 'relative',
          }}
          onClick={(isTrainingView && !isCompleted) ? undefined : handleCardClick}
          disabled={isTrainingView && !isCompleted}
          >
          <div style={{ 
            width: '100%', 
            height: '100%', 
            background: '#111111',
            borderRadius: 16,
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            overflow: 'hidden' 
          }}>
            {currentItem?.id === item.id && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: SECTION_WIDTH,
                height: 120,
                border: '5px solid #F8585C',
                borderRadius: 16,
                pointerEvents: 'none',
                zIndex: 6,
              }} />
            )}

            {/* Upload Progress Indicator */}
            {(asset.type === 'video' || asset.type === 'audio') && (
              <div style={{
                position: 'absolute',
                top: (isUploading || isUploadCompleted) ? '50%' : 8,
                left: (isUploading || isUploadCompleted) ? '50%' : 'auto',
                right: (isUploading || isUploadCompleted) ? 'auto' : 8,
                transform: (isUploading || isUploadCompleted) ? 'translate(-50%, -50%)' : 'none',
                zIndex: 10,
              }}>
                <CircularProgress
                  assetId={asset.id}
                  size={(isUploading || isUploadCompleted) ? 96 : 48}
                  strokeWidth={(isUploading || isUploadCompleted) ? 6 : 4}
                  onRetry={(assetId) => {
                    dispatch(retryUpload({ assetId }));
                  }}
                />
              </div>
            )}
            {/* Completed check mark badge - shown ONLY in training view, NOT in edit mode */}
            {isCompleted && isTrainingView && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: '#10B981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 7,
                pointerEvents: 'none',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4)',
                opacity: 1,
                transition: 'opacity 0.3s ease-in',
              }}>
                <img 
                  src={checkmarkIcon} 
                  alt="Completed" 
                  style={{
                    width: 36,
                    height: 36,
                    filter: 'brightness(0) invert(1)',
                  }}
                />
              </div>
            )}
            {(() => {
              if (asset.type === 'video' || asset.type === 'image') {
                return (
                  <img
                    src={asset.thumbnail_url}
                    alt={asset.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 16 }}
                  />
                );
              }
              if (asset.type === 'audio') {
                return (
                  <img
                    src={asset.thumbnail_url}
                    alt="Audio"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 16 }}
                  />
                );
              }
              if (asset.type === 'text') {
                return (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: '#F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 16,
                      padding: 16,
                    }}
                  >
                    <img
                      src={notesIcon}
                      alt="Text"
                      style={{ width: 80, height: 'auto' }}
                    />
                  </div>
                );
              }
              if (asset.type === 'question') {
                return (
                  <img
                    src={questionSectionImg}
                    alt="question"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 16 }}
                  />
                );
              }
              if (asset.type === 'assigned-section') {
                return (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: '#F3F4F6',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 16,
                      padding: 16,
                    }}
                  >
                    <img
                      src={assignIcon}
                      alt="Assigned Section"
                      style={{ width: 60, height: 60, objectFit: 'contain', marginBottom: 8 }}
                    />
                    <span style={{ fontSize: 12, color: '#6B7280', textAlign: 'center' }}>
                      Section Assigned
                    </span>
                  </div>
                );
              }
              if (asset.type === 'placeholder') {
                const showPlaceholderAddButton = !isTemplateMode && onPlaceholderAddContent;
                const placeholderLabel = isTemplateMode ? 'Placeholder' : 'Add content';
                return (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: '#F9FAFB',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 16,
                      padding: '18px 20px',
                      border: '2px dashed #9CA3AF',
                      boxSizing: 'border-box',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <PlaceholderPatternOverlay
                      color="rgba(107, 114, 128, 0.06)"
                      fontSize={16}
                      rowGap={12}
                    />
                    {showPlaceholderAddButton ? (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onPlaceholderAddContent(item, e.currentTarget);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onPlaceholderAddContent(item, e.currentTarget);
                          }
                        }}
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: showPlaceholderAddButton ? '50%' : 14,
                          transform: showPlaceholderAddButton
                            ? 'translate(-50%, -50%)'
                            : 'translateX(-50%)',
                          width: 52,
                          height: 52,
                          borderRadius: '50%',
                          background: '#F8585C',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0,
                          zIndex: 1,
                        }}
                        title="Add content for this placeholder"
                        aria-label="Add content for this placeholder"
                      >
                        <svg width="20" height="20" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <line x1="7" y1="1.5" x2="7" y2="12.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
                          <line x1="1.5" y1="7" x2="12.5" y2="7" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </div>
                    ) : (
                      <div style={{
                        width: 52,
                        minWidth: 52,
                        height: 52,
                        minHeight: 52,
                        flexShrink: 0,
                        borderRadius: '50%',
                        background: 'rgba(248, 88, 92, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 10,
                        zIndex: 1,
                      }}>
                        <TemplatesIcon size={22} stroke="#F8585C" strokeWidth={1.8} />
                      </div>
                    )}
                    <span style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#6B7280',
                      textAlign: 'center',
                      lineHeight: '18px',
                      maxWidth: 150,
                      marginTop: showPlaceholderAddButton ? 44 : 0,
                      visibility: showPlaceholderAddButton ? 'hidden' : 'visible',
                      zIndex: 1,
                    }}>
                      {placeholderLabel}
                    </span>
                  </div>
                );
              }
              if (asset.type === 'document') {
                const documentIcon = getDocumentIcon(
                  asset?.metadata?.attachmentFileName || asset.name,
                  asset?.metadata?.attachmentMimeType || asset.mime_type
                );
                return (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: '#F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 16,
                      padding: 16,
                    }}
                  >
                    <img
                      src={documentIcon}
                      alt="Document"
                      style={{ width: 100, height: 100, objectFit: 'contain' }}
                    />
                  </div>
                );
              }
              return <span style={{ fontSize: 24, color: '#ffffff' }}>📄</span>;
            })()}
          </div>
          {/* Save section (favorite) */}
          {(() => {
            // Don't show star icon in preview mode
            if (isPreviewMode) return null;

            // Never show for assigned-section placeholders
            if (asset.type === 'assigned-section') return null;

            if (asset.type === 'placeholder') return null;
            
            // Hide during upload
            if (isUploading) return null;
            
            // Contributors cannot save sections they didn't create
            if (!isCreator && !canInteractWithSection) return null;
            
            const sectionId = item.metadata?.sectionId;
            // Read saved status directly from asset metadata (Redux state)
            const isSaved = asset.metadata?.isSaved || false;
            const isPending = pendingSaveItemIds.has(item.id);
            const isPersisted = isEditMode && !!sectionId;
            
            // Show button if: saved (always visible), or hovering
            if (!isSaved && !isHovered) return null;
            
            let tooltipText;
            let iconOpacity = 1;
            
            if (isSaved) {
              tooltipText = 'Saved to your library';
              iconOpacity = 1;
            } else if (isSaving) {
              tooltipText = 'Saving section...';
              iconOpacity = 0.6;
            } else if (isPersisted) {
              tooltipText = 'Save this section to your library';
              iconOpacity = 1;
            } else if (isPending) {
              tooltipText = 'Will appear in Saved Sections after publish (click to remove)';
              iconOpacity = 0.9;
            } else {
              tooltipText = 'Save this section after publishing';
              iconOpacity = 0.4;
            }
            
            const handleClick = async (e) => {
              e.stopPropagation();
              if (isSaved || isSaving) {
                return;
              }

              if (isPersisted) {
                // Save immediately for persisted sections
                setIsSaving(true);
                try {
                  await SectionService.saveSection(sectionId);
                  // Update asset in Redux to mark as saved
                  dispatch(updateAsset({ id: asset.id, changes: { metadata: { ...asset.metadata, isSaved: true } } }));
                  setSavedPopup({ itemId: item.id, visible: true });
                  setTimeout(() => setSavedPopup({ itemId: null, visible: false }), 3000);
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error('Save section failed', err);
                } finally {
                  setIsSaving(false);
                }
              } else {
                // Toggle pending save for new sections
                setPendingSaveItemIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(item.id)) {
                    next.delete(item.id);
                  } else {
                    next.add(item.id);
                  }
                  return next;
                });
              }
            };
            
            return (
              <div
                style={{
                  position: 'absolute',
                  left: 24,
                  top: 24,
                  zIndex: 10,
                }}
              >
                {tooltipText ? (
                  <Tooltip
                    width={220}
                    position="top-right"
                    text={tooltipText}
                  >
                    <button
                      type="button"
                      onClick={handleClick}
                      style={{
                        padding: 0,
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: isSaved ? 'default' : 'pointer',
                        opacity: iconOpacity,
                      }}
                    >
                      <img src={saveIcon} alt="Save" />
                    </button>
                  </Tooltip>
                ) : (
                  <button
                    type="button"
                    onClick={handleClick}
                    style={{
                      padding: 0,
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: isSaved || isSaving ? 'default' : 'pointer',
                      opacity: iconOpacity,
                      position: 'relative',
                    }}
                  >
                    <img src={saveIcon} alt="Save" />
                    {isSaving && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 5,
                      }}>
                        <CircularProgress
                          size={20}
                          strokeWidth={2}
                        />
                      </div>
                    )}
                  </button>
                )}
              </div>
            );
          })()}
          {/* Edit button - appears on hover for video items */}
          {/* Contributors can only edit sections they created */}
          {/* Hide during upload */}
          {!isPreviewMode && !isUploading && isHovered && isVideo && (isCreator || canInteractWithSection) && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                // Directly open trim modal instead of showing the "What would you like to do" modal
                if (asset && asset.type === 'video') {
                  // Calculate original duration (duration + trim_start + trim_end)
                  const trimStart = item.settings?.trim_start || 0;
                  const trimEnd = item.settings?.trim_end || 0;
                  const originalDuration =
                    asset.duration ||
                    item.duration + trimStart + trimEnd;
                  dispatch(setTrimModalData({
                    itemId: item.id,
                    asset,
                    itemDuration: originalDuration,
                    existingTrimStart: trimStart,
                    existingTrimEnd: trimEnd,
                  }));
                }
              }}
              data-testid={`edit-section-button-${item.id}`}
              style={{
                position: 'absolute',
                right: 24,
                bottom: 24,
                padding: 0,
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                zIndex: 10,
              }}
            >
              <img src={editIcon} alt="Edit" />
            </button>
          )}
          {!isPreviewMode && isTemplateMode && isHovered && asset.type === 'placeholder' && (isCreator || canInteractWithSection) && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditPlaceholder(asset, item);
              }}
              data-testid={`edit-placeholder-button-${item.id}`}
              style={{
                position: 'absolute',
                right: 24,
                top: 24,
                padding: 0,
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                zIndex: 10,
              }}
            >
              <img src={editIcon} alt="Edit" />
            </button>
          )}
        </button>
        </div>
        {/* User icon - shown for assigned sections and contributed sections */}
        {/* Positioned outside the draggable wrapper to avoid clipping */}
        {userIconData && (
          <div
            style={{
              position: 'absolute',
              bottom: -18,
              right: -18,
              width: 50,
              height: 50,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'white',
              borderRadius: '50%',
              filter: userIconData.photoPath ? 'none' : 'invert(100%)',
              zIndex: 10,
              pointerEvents: 'auto',
            }}
            onMouseOver={() => setIsUserIconHovered(true)}
            onMouseLeave={() => setIsUserIconHovered(false)}
          >
            <img
              src={getUserPhotoUrl(userIconData.photoPath) || usericon}
              alt="usericon"
              style={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
            {isUserIconHovered && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: 8,
                  zIndex: 11,
                  color: 'white',
                  backgroundColor: '#111827',
                  paddingLeft: 15,
                  paddingRight: 15,
                  borderRadius: 15,
                  whiteSpace: 'nowrap',
                  fontSize: 14,
                  paddingTop: 6,
                  paddingBottom: 6,
                }}
              >
                {userIconData.name}
              </div>
            )}
          </div>
        )}
        {/* Title container - positioned absolutely relative to <li> */}
        <div style={{ 
          position: 'absolute',
          left: 0,
          bottom: -50,
          width: '100%',
          height: 24,
          paddingLeft: 18,
          paddingRight: 18,
          borderRadius: 16,
          display: 'flex',
          alignItems: 'center',
        }}>
          {!isPreviewMode && !isLockedPlaceholderTitle ? (
            <input
              type="text"
              value={asset.name || ''}
              onChange={(e) => {
                e.stopPropagation();
                handleSectionTitleChange(asset.id, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={() => dispatch(setFocusedSectionTitleId(asset.id))}
              onBlur={() => dispatch(setFocusedSectionTitleId(null))}
              onMouseEnter={() => dispatch(setHoveredSectionTitleId(asset.id))}
              onMouseLeave={() => dispatch(setHoveredSectionTitleId(null))}
              style={{
                width: '100%',
                fontFamily: 'Poppins',
                fontSize: 22,
                fontWeight: 500,
                lineHeight: '33px',
                textAlign: 'center',
                color: titleColor,
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: (focusedSectionTitleId === asset.id || hoveredSectionTitleId === asset.id) ? '2px dashed #111111' : 'none',
                outline: 'none',
                padding: 0,
                margin: 0,
                marginBottom: (focusedSectionTitleId === asset.id || hoveredSectionTitleId === asset.id) ? '-2px' : 0,
              }}
              disabled={isPreviewMode}
            />
          ) : (
            <div style={{ fontSize: 22, fontWeight: 500, color: titleColor, width: '100%', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.name}</div>
          )}
        </div>
        {/* Success popup for saved sections - positioned outside overflow container */}
        {savedPopup.visible && savedPopup.itemId === item.id && (
          <div
            className={styles.saveSuccessPopup}
            style={{
              top: -40,
              left: 40,
              transform: 'translateX(-50%)',
            }}
          >
            Section saved<br />successfully
          </div>
        )}
      </li>
    </>
  );
}
